import React from 'react';
import { Link } from 'react-router-dom';
import {
  Atom,
  BookOpen,
  Building,
  TrendingUp,
  Globe2,
  Layers,
  MessageCircle,
  Rocket,
  Shield,
  Sparkles
} from 'lucide-react';

const PinIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" {...props}>
    <path
      d="M9.5 3H14.5L15 10L17 12V14H12L12 21L10.5 21L10 14H7V12L9 10L9.5 3Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ABOUT_SECTIONS = [
  {
    icon: Sparkles,
    title: 'We Personalize Your Learning',
    content:
      'Axora tailors your dashboard to your role, goals, and skill level. You receive curated CPD courses, AI-recommended resources, and a structured path designed for your unique journey in engineering or architecture.'
  },
  {
    icon: Shield,
    title: 'We Standardize Your Knowledge',
    content:
      'Global best practices, industry standards, and accredited content live in one place—helping you stay aligned with international expectations and grow professionally with confidence.'
  },
  {
    icon: Layers,
    title: 'We Help You Build Real Skills',
    content:
      'Hands-on design challenges, CPD assignments, and guided projects sharpen both technical and creative capabilities while earning recognized CPD credits.'
  },
  {
    icon: TrendingUp,
    title: 'We Support Continuous Growth',
    content:
      'Track streaks, XP, achievements, CPD hours, and competencies—your progress is clear, measurable, and ready to share with mentors, employers, and peers.'
  },
  {
    icon: Rocket,
    title: 'We Connect You to Opportunity',
    content:
      'AI-matched global competitions, team challenges, and merit-based showcases help you prove your talent, access top-tier opportunities, and build a global portfolio.'
  }
];

const SERVICES = [
  {
    icon: BookOpen,
    title: 'Study Hub',
    bullets: [
      'Accredited CPD courses & learning paths',
      'Research libraries and global resources',
      'AI-driven content recommendations',
      'Standardized tests and assessments',
      'Progress tracking & competency dashboards'
    ]
  },
  {
    icon: Building,
    title: 'Studio Spaces',
    bullets: [
      'Collaborate across universities, firms, and teams',
      'Anonymous peer-review and moderated critique',
      'Global classrooms with role-based access',
      'Faculty-led or private studio environments',
      'Approval-based entry for security and quality'
    ]
  },
  {
    icon: PinIcon,
    title: 'Digital Pin-Up',
    bullets: [
      'Prepare for juries and finals with real-time feedback',
      'Cross-department critique to align design standards',
      'Firms maintain quality through shared review boards',
      'Transparent documentation for every critique',
      'Structured notes for future improvement'
    ]
  },
  {
    icon: MessageCircle,
    title: 'Axora Chat',
    bullets: [
      'Role-based AI assistants for research & study support',
      'Group chats for classes, teams, or professional cohorts',
      'Personal knowledge archives and searchable history',
      'Mentor, peer, and employer updates in one place'
    ]
  },
  {
    icon: Globe2,
    title: 'Concept Explorer',
    bullets: [
      'Sustainability, AI, mega-structure trends and insights',
      'Market analysis and emerging forecasts',
      'Global case studies and architectural theories',
      'Real-world project references and innovation tracking'
    ]
  },
  {
    icon: Atom,
    title: 'Opportunity Engine',
    bullets: [
      'AI-matched global competitions & challenges',
      'Team collaborations with major organizations',
      'Early alerts for jobs, grants, and residency programs',
      'Portfolio-building through real client briefs'
    ]
  }
];

const WHY_AXORA = [
  'Learning is personalized—no more generic or overwhelming content dumps.',
  'Knowledge is standardized—accredited, globally aligned, and quality checked.',
  'Growth is transparent—you can measure, share, and celebrate every milestone.',
  'Talent wins—opportunities are matched by merit and potential, not connections.',
  'Professionals get modern tools—CPD paths, AI copilots, design studios, analytics, and more.'
];

const AboutUs = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-[#FFF6F6] to-white text-gray-900">
      <header className="border-b border-[#E6B6B6] bg-white/80 backdrop-blur sticky top-0 z-50">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 sm:px-6 py-3 sm:py-5">
          <Link to="/" className="text-xl sm:text-2xl font-semibold tracking-wide text-[#AC5757]">
            AXORA
          </Link>

          <nav className="hidden items-center gap-6 text-sm font-medium text-gray-600 md:flex">
            <a href="/#features" className="hover:text-[#AC5757]">
              Features
            </a>
            <a href="/#services" className="hover:text-[#AC5757]">
              Services
            </a>
            <a href="#why" className="hover:text-[#AC5757]">
              Why Axora
            </a>
            <Link to="/pricing" className="hover:text-[#AC5757]">
              Pricing
            </Link>
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login" className="text-xs sm:text-sm font-medium text-gray-600 hover:text-[#AC5757] px-2">
              Sign in
            </Link>
            <Link
              to="/signup"
              className="rounded-full bg-[#AC5757] px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold text-white shadow-sm hover:bg-[#8A4A4A] whitespace-nowrap"
            >
              Join Axora
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-8 sm:py-12 md:py-16 space-y-12 sm:space-y-16 md:space-y-20">
        <section className="space-y-4 sm:space-y-5 text-center">
          <span className="text-[0.65rem] sm:text-xs uppercase tracking-[0.3em] sm:tracking-[0.4em] text-[#AC5757]">About Axora</span>
          <h1 className="font-judson text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight px-2">
            Building the future of CPD for engineers and architects
          </h1>
          <p className="mx-auto max-w-3xl text-sm sm:text-base md:text-lg text-gray-600 px-2">
            Axora is an AI-powered professional development platform built to elevate engineers, architects, and future
            designers through standardized knowledge, personalized learning, and real-world growth opportunities. We
            exist to make your learning journey clearer, faster, and globally recognized—no matter where you are in your
            career.
          </p>
        </section>

        <section className="grid gap-4 sm:gap-6 md:gap-8 md:grid-cols-2">
          <div className="rounded-2xl sm:rounded-3xl bg-white p-5 sm:p-6 md:p-8 shadow-lg">
            <h2 className="font-judson text-xl sm:text-2xl font-semibold text-[#AC5757]">Our Purpose</h2>
            <p className="mt-3 sm:mt-4 text-sm sm:text-base text-gray-600">
              Professional development shouldn’t be confusing, inaccessible, or based on luck. Axora gives you structure,
              guidance, and access to the kind of learning ecosystem that was once available only to top institutions and
              large firms. Whether you’re a student, educator, practicing professional, or global organization, Axora
              helps you grow with confidence and stay competitive in a rapidly evolving industry.
            </p>
          </div>
          <div className="rounded-2xl sm:rounded-3xl bg-[#AC5757] p-5 sm:p-6 md:p-8 text-white shadow-lg">
            <h2 className="font-judson text-xl sm:text-2xl font-semibold">What Makes Axora Different?</h2>
            <p className="mt-3 sm:mt-4 text-sm sm:text-base text-[#F7EDED]">
              We combine accredited CPD content, AI-guided pathways, collaborative studio spaces, and real-world
              challenges—delivered through a single platform that keeps your learning personalized, your progress
              verifiable, and your portfolio ready for the global stage.
            </p>
            <Link
              to="/pricing"
              className="mt-4 sm:mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-white px-4 sm:px-5 py-2 text-xs sm:text-sm font-semibold text-[#AC5757] shadow hover:bg-[#FDF6F6] w-full sm:w-auto"
            >
              Explore pricing
            </Link>
          </div>
        </section>

        <section id="what-we-do" className="space-y-6 sm:space-y-8 md:space-y-10">
          <div className="text-center px-2">
            <p className="text-[0.65rem] sm:text-xs uppercase tracking-[0.25em] sm:tracking-[0.3em] text-[#AC5757]">What Axora Does For You</p>
            <h2 className="mt-2 font-judson text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold leading-tight">Personalized, structured, real-world growth</h2>
          </div>
          <div className="grid gap-4 sm:gap-5 md:gap-6 md:grid-cols-2 xl:grid-cols-3">
            {ABOUT_SECTIONS.map((section) => (
              <div key={section.title} className="rounded-2xl sm:rounded-3xl border border-[#F3C7C7] bg-white p-4 sm:p-5 md:p-6 shadow">
                <section.icon className="text-[#AC5757]" size={24} />
                <h3 className="mt-3 sm:mt-4 text-lg sm:text-xl font-semibold">{section.title}</h3>
                <p className="mt-2 sm:mt-3 text-xs sm:text-sm leading-relaxed text-gray-600">{section.content}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="services" className="space-y-6 sm:space-y-8 md:space-y-10">
          <div className="text-center px-2">
            <p className="text-[0.65rem] sm:text-xs uppercase tracking-[0.25em] sm:tracking-[0.3em] text-[#AC5757]">Our Core Services</p>
            <h2 className="mt-2 font-judson text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold leading-tight">
              Designed for learning, collaboration, and opportunity
            </h2>
          </div>
          <div className="grid gap-4 sm:gap-5 md:gap-6 md:grid-cols-2 xl:grid-cols-3">
            {SERVICES.map((service) => (
              <div key={service.title} className="rounded-2xl sm:rounded-3xl border border-[#F3C7C7] bg-white p-4 sm:p-5 md:p-6 shadow">
                <service.icon size={24} className="text-[#AC5757]" />
                <h3 className="mt-3 sm:mt-4 text-lg sm:text-xl font-semibold">{service.title}</h3>
                <ul className="mt-3 sm:mt-4 space-y-2 sm:space-y-3 text-xs sm:text-sm leading-relaxed text-gray-600">
                  {service.bullets.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section id="why" className="rounded-2xl sm:rounded-3xl bg-[#AC5757] px-5 sm:px-6 md:px-8 py-8 sm:py-10 md:py-12 text-white shadow-lg">
          <h2 className="font-judson text-2xl sm:text-3xl md:text-4xl font-bold">Why Axora?</h2>
          <p className="mt-3 sm:mt-4 text-sm sm:text-base md:text-lg text-[#F7EDED]">
            The world needs a platform where continuous professional development is accessible, standardized, and aligned
            with real-world opportunities. Axora delivers that by combining structured learning, intelligent insights, and
            a global network of mentors, collaborators, and industry-grade challenges.
          </p>
          <ul className="mt-6 space-y-3 text-sm md:text-base">
            {WHY_AXORA.map((reason) => (
              <li key={reason} className="flex items-start gap-3">
                <span className="mt-2 inline-block h-2.5 w-2.5 rounded-full bg-white/90" />
                <span>{reason}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-2xl sm:rounded-3xl border border-dashed border-[#E6B6B6] p-5 sm:p-6 md:p-8 text-center shadow-sm">
          <h2 className="font-judson text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold leading-tight">Ready to make your CPD journey clearer?</h2>
          <p className="mx-auto mt-3 sm:mt-4 max-w-3xl text-xs sm:text-sm md:text-base text-gray-600 px-2">
            Whether you want to explore tailored learning, build a studio cohort, or launch global challenges, Axora keeps
            you prepared, supported, and connected. Let’s grow with confidence—together.
          </p>
          <div className="mt-5 sm:mt-6 flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-4">
            <Link
              to="/signup"
              className="rounded-full bg-[#AC5757] px-5 sm:px-6 py-2.5 sm:py-3 text-xs sm:text-sm font-semibold text-white shadow hover:bg-[#8A4A4A] w-full sm:w-auto text-center"
            >
              Join Axora
            </Link>
            <Link
              to="/login"
              className="rounded-full border border-[#AC5757] px-5 sm:px-6 py-2.5 sm:py-3 text-xs sm:text-sm font-semibold text-[#AC5757] hover:bg-[#AC5757]/10 w-full sm:w-auto text-center"
            >
              Already part of Axora?
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
};

export default AboutUs;

